import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import BlockIcon from "@material-ui/icons/Block";
import VisibilityIcon from "@material-ui/icons/Visibility";

import List from "../../components/List";
import Sidebar from "../../components/SideBar";
import EventApi from "../../services/event";
import WhatsAppService from "../../services/whatsapp";
import { config } from "../../config";
import EmailLambda from "../../services/emailLambda";

// ── Formatters ────────────────────────────────────────────────────────────────

function formatDocument(cpf) {
    if (!cpf) return "-";
    if (cpf.startsWith("PASS-")) {
        return cpf.replace("PASS-", ""); // exibe passaporte sem prefixo
    }
    // formata CPF: 99999999999 → 999.999.999-99
    const digits = cpf.replace(/\D/g, "");
    if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return cpf;
}

function formatDate(dateStr) {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── Column definitions ────────────────────────────────────────────────────────

function buildColumns(sponsorsMap) {
    return [
        { title: "Nome", field: "name" },
        { title: "E-mail", field: "email" },
        {
            title: "CPF / Passaporte",
            field: "cpf",
            render: (row) => formatDocument(row.cpf),
        },
        { title: "Empresa", field: "company" },
        { title: "Cargo", field: "role" },
        {
            title: "Patrocinador",
            field: "sponsor_id",
            render: (row) => (row.sponsor_id ? (sponsorsMap[row.sponsor_id] || "-") : ""),
        },
        {
            title: "Data de Inscrição",
            field: "created_at",
            render: (row) => formatDate(row.created_at),
        },
    ];
}

// ── PersonDetail – modal read-only ───────────────────────────────────────────

function formatPhone(phone) {
    if (!phone) return "-";
    const d = String(phone).replace(/\D/g, "");
    if (d.length === 13) {
        // +55 (99) 9 9999-9999
        return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 5)} ${d.slice(5, 9)}-${d.slice(9)}`;
    }
    if (d.length === 12) {
        // +55 (99) 9999-9999
        return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
    }
    return phone;
}

const DetailRow = ({ label, value }) => (
    <div style={{ marginBottom: 14 }}>
        <div style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "#9ca3af",
            marginBottom: 2,
        }}>
            {label}
        </div>
        <div style={{
            fontSize: 14,
            color: "#111827",
            wordBreak: "break-word",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            padding: "7px 10px",
            minHeight: 34,
        }}>
            {value || <span style={{ color: "#d1d5db" }}>—</span>}
        </div>
    </div>
);

const SectionHeader = ({ children }) => (
    <div style={{
        fontSize: 11,
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        color: "#6366f1",
        borderBottom: "1px solid #e5e7eb",
        paddingBottom: 6,
        marginTop: 20,
        marginBottom: 14,
    }}>
        {children}
    </div>
);

function PersonDetail({ person, sponsorsMap }) {
    const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" };

    const isPassport = person.cpf && person.cpf.startsWith("PASS-");
    const docLabel = isPassport ? "Passaporte" : "CPF";
    const docValue = formatDocument(person.cpf);

    const sponsorName = person.sponsor_id ? (sponsorsMap[person.sponsor_id] || person.sponsor_id) : null;

    return (
        <div>
            <SectionHeader>Dados Pessoais</SectionHeader>
            <div style={grid2}>
                <DetailRow label="Nome Completo" value={person.name} />
                <DetailRow label="Nome do Crachá" value={person.name_tag} />
            </div>
            <div style={grid2}>
                <DetailRow label={docLabel} value={docValue} />
                <DetailRow label="Gênero" value={person.gender} />
            </div>
            <div style={grid2}>
                <DetailRow label="Estado" value={person.address || (isPassport ? "Estrangeiro" : null)} />
                <DetailRow label="Categoria" value={person.event_category} />
            </div>

            <SectionHeader>Contato</SectionHeader>
            <div style={grid2}>
                <DetailRow label="E-mail" value={person.email} />
                <DetailRow label="Telefone" value={formatPhone(person.phone)} />
            </div>

            <SectionHeader>Dados Profissionais</SectionHeader>
            <div style={grid2}>
                <DetailRow label="Empresa / Instituição" value={person.company} />
                <DetailRow label="Cargo" value={person.role} />
            </div>
            <div style={grid2}>
                <DetailRow label="Setor" value={person.extras} />
                <DetailRow label="Área" value={person.custom_fields} />
            </div>
            {sponsorName && (
                <DetailRow label="Patrocinador" value={sponsorName} />
            )}

            <SectionHeader>Inscrição</SectionHeader>
            <div style={grid2}>
                <DetailRow label="Data de Inscrição" value={formatDate(person.created_at)} />
                <DetailRow label="Data de Aprovação" value={formatDate(person.approved_at)} />
            </div>
            <div style={grid2}>
                <DetailRow label="ID" value={person.id} />
                <DetailRow label="Status" value={person.solicitation_approved ? "Aprovado" : "Pendente"} />
            </div>
        </div>
    );
}

// ── Component ─────────────────────────────────────────────────────────────────

const PendingSubscriptions = (props) => {
    const eventId =
        props?.computedMatch?.params?.event_id ||
        props?.match?.params?.event_id ||
        1;

    const tableRef = useRef(null);
    const [tableKey, setTableKey] = useState(0);
    const [sponsorsMap, setSponsorsMap] = useState({});

    // Força re-mount do MaterialTable na página 0, evitando loop de paginação
    // quando a página atual fica vazia após uma ação (approve/reject)
    function refreshTable() {
        setTableKey((k) => k + 1);
    }

    // Carrega patrocinadores uma vez para exibir o nome na coluna
    useEffect(() => {
        EventApi.listSponsors({ event_id: eventId }).then((resp) => {
            if (resp?.data?.data) {
                const map = {};
                resp.data.data.forEach((s) => { map[s.id] = s.name; });
                setSponsorsMap(map);
            }
        }).catch(() => { });
    }, [eventId]);

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        person: null,
        action: null, // "approve" | "reject"
    });

    const [viewPerson, setViewPerson] = useState(null);

    // ── Data fetching ──────────────────────────────────────────────────────

    const getRows = async (page = 1, perPage = 10, search = "") => {
        try {
            const params = {
                page,
                perPage,
                solicitation_approved: false,
                // Exclui inscrições marcadas como rejeitadas
                payment_status_not: "rejected",
            };
            if (search) {
                const trimmed = search.trim();
                // Detecta CPF (somente dígitos ou com máscara)
                const onlyDigits = trimmed.replace(/\D/g, "");
                const emailRegex = /^[^\s@]+@[^\s@]+/;
                if (emailRegex.test(trimmed)) {
                    params.email = trimmed;
                } else if (onlyDigits.length >= 6 && trimmed === onlyDigits) {
                    params.cpf = trimmed;
                } else {
                    params.name = trimmed;
                }
            }

            const resp = await EventApi.getAllPersonByEvent(eventId, params);
            if (!resp || !resp.data) {
                return { rows: [], total: 0 };
            }

            const data = resp.data.data || (Array.isArray(resp.data) ? resp.data : []);
            const total = resp.data.total || data.length;

            return { rows: data, total };
        } catch (error) {
            console.error("Erro ao carregar inscrições pendentes:", error);
            toast.error("Erro ao carregar inscrições pendentes");
            return { rows: [], total: 0 };
        }
    };

    // ── Dialog helpers ─────────────────────────────────────────────────────

    function openConfirmDialog(person, action) {
        setConfirmDialog({ open: true, person, action });
    }

    function closeConfirmDialog() {
        setConfirmDialog({ open: false, person: null, action: null });
    }

    async function handleConfirm() {
        const { person, action } = confirmDialog;
        closeConfirmDialog();
        if (action === "approve") {
            await handleApprove(person);
        } else if (action === "reject") {
            await handleReject(person);
        }
    }

    // ── Approve ────────────────────────────────────────────────────────────

    async function handleApprove(person) {
        try {
            const resp = await EventApi.updatePerson(person.id, {
                ...person,
                solicitation_approved: true,
                approved_at: new Date().toISOString(),
            });

            if (resp && resp.data) {
                toast.success("Inscrição aprovada com sucesso");
                refreshTable();

                // Envia e-mail de confirmação (fire-and-forget)
                const qrcode = `${config.QRCODE_HOST}/?size=200x200&data=${person.id}`;
                EventApi.sendConfirmationMail(person.name, person.email, qrcode)
                    .catch((err) => console.error("Erro ao enviar e-mail de confirmação:", err));

                // Envia WhatsApp de confirmação (fire-and-forget)
                WhatsAppService.sendConfirmacaoInscricao({
                    phone: person.phone,
                    personId: person.id,
                }).catch((err) => console.warn("[WhatsApp] Falha no envio:", err?.message ?? err));
            } else {
                toast.error("Erro ao aprovar inscrição. Tente novamente");
            }
        } catch (error) {
            console.error("Erro ao aprovar:", error);
            toast.error("Erro ao aprovar inscrição. Tente novamente");
        }
    }

    // ── Reject ─────────────────────────────────────────────────────────────

    async function handleReject(person) {
        try {
            const resp = await EventApi.patchPerson(person.id, {
                payment_status: "rejected",
            });

            if (resp && resp.data) {
                // Envia e-mail de recusa (fire-and-forget)
                EmailLambda.sendEmail({
                    to: person.email,
                    subject: "ANM 2026",
                    mail_kind: "reject_email",
                }).catch((err) => console.error("Erro ao enviar e-mail de recusa:", err));

                toast.success("Inscrição recusada com sucesso");
                refreshTable();
            } else {
                toast.error("Erro ao recusar inscrição. Tente novamente");
            }
        } catch (error) {
            console.error("Erro ao recusar:", error);
            toast.error("Erro ao recusar inscrição. Tente novamente");
        }
    }

    // ── Actions ────────────────────────────────────────────────────────────

    const actions = [
        {
            icon: () => <VisibilityIcon style={{ color: "#6366f1" }} />,
            tooltip: "Ver dados completos",
            onClick: (e, person) => setViewPerson(person),
        },
        {
            icon: () => <CheckIcon style={{ color: "#22c55e" }} />,
            tooltip: "Aprovar inscrição",
            onClick: (e, person) => openConfirmDialog(person, "approve"),
        },
        {
            icon: () => <BlockIcon style={{ color: "#ef4444" }} />,
            tooltip: "Recusar inscrição",
            onClick: (e, person) => openConfirmDialog(person, "reject"),
        },
    ];

    // ── Confirmation dialog messages ───────────────────────────────────────

    const dialogTitle =
        confirmDialog.action === "approve"
            ? "Confirmar Aprovação"
            : "Confirmar Recusa";

    const dialogMessage =
        confirmDialog.action === "approve"
            ? `Deseja realmente aprovar a inscrição de ${confirmDialog.person?.name}?`
            : `Deseja realmente recusar a inscrição de ${confirmDialog.person?.name}? O registro será movido para a lista de recusados.`;

    // ── Render ─────────────────────────────────────────────────────────────

    const page = (
        <>
            <List
                key={tableKey}
                tableRef={tableRef}
                columns={buildColumns(sponsorsMap)}
                actions={actions}
                getRows={getRows}
            />

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{dialogMessage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirmDialog} color="default">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        color={confirmDialog.action === "approve" ? "primary" : "secondary"}
                        variant="contained"
                        autoFocus
                    >
                        {confirmDialog.action === "approve" ? "Aprovar" : "Recusar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Person Dialog */}
            <Dialog
                open={Boolean(viewPerson)}
                onClose={() => setViewPerson(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{ borderBottom: "1px solid #e5e7eb", paddingBottom: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: 18 }}>Dados da Inscrição</span>
                    {viewPerson?.name && (
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                            {viewPerson.name}
                        </div>
                    )}
                </DialogTitle>
                <DialogContent style={{ paddingTop: 20 }}>
                    {viewPerson && <PersonDetail person={viewPerson} sponsorsMap={sponsorsMap} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewPerson(null)} color="default" variant="outlined">
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );

    return <Sidebar current="Inscrições Pendentes" content={page} />;
};

export default PendingSubscriptions;
